import { useState, useEffect } from "react";
import { 
  TrendingUp, 
  DollarSign, 
  Clock, 
  Users,
  ArrowUp,
  ArrowDown,
  Calendar,
  PieChart,
  BarChart3,
  Target
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AnalyticsPanel = ({ token }) => {
  const [performance, setPerformance] = useState(null);
  const [leadSources, setLeadSources] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("performance");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [perfRes, sourcesRes] = await Promise.all([
        axios.get(`${API}/owner/analytics/performance?months=6`, { headers }),
        axios.get(`${API}/owner/analytics/lead-sources`, { headers })
      ]);
      setPerformance(perfRes.data);
      setLeadSources(sourcesRes.data);
    } catch (err) {
      console.error("Error fetching analytics:", err);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const totals = performance?.totals || { revenue: 0, jobs: 0, avg_job_value: 0 };
  const monthly = performance?.monthly || [];
  const bySource = leadSources?.by_source || {};

  // Calculate trends (compare last 2 months)
  const recentMonths = monthly.slice(-2);
  const getTrend = (field) => {
    if (recentMonths.length < 2) return { value: 0, positive: true };
    const current = recentMonths[1]?.[field] || 0;
    const previous = recentMonths[0]?.[field] || 1;
    const change = previous > 0 ? ((current - previous) / previous) * 100 : 0;
    return { value: Math.abs(Math.round(change)), positive: change >= 0 };
  };

  const revenueTrend = getTrend("revenue");
  const jobsTrend = getTrend("jobs_completed");

  const sourceColors = [
    "bg-blue-500", "bg-green-500", "bg-yellow-500", 
    "bg-purple-500", "bg-pink-500", "bg-indigo-500"
  ];

  const formatSourceName = (source) => {
    return source
      .replace(/_/g, " ")
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="p-6" data-testid="analytics-panel">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
        <p className="text-gray-600">Performance metrics and lead source insights</p>
      </div>

      {/* View Toggle */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={activeView === "performance" ? "default" : "outline"}
          onClick={() => setActiveView("performance")}
        >
          <BarChart3 className="w-4 h-4 mr-2" />
          Performance
        </Button>
        <Button
          variant={activeView === "sources" ? "default" : "outline"}
          onClick={() => setActiveView("sources")}
        >
          <PieChart className="w-4 h-4 mr-2" />
          Lead Sources
        </Button>
      </div>

      {activeView === "performance" && (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl p-5 shadow-sm border">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-green-100 rounded-lg">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <div className={`flex items-center text-xs ${revenueTrend.positive ? "text-green-600" : "text-red-600"}`}>
                  {revenueTrend.positive ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                  {revenueTrend.value}%
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">£{totals.revenue.toLocaleString()}</p>
              <p className="text-sm text-gray-500">Total Revenue</p>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Target className="w-5 h-5 text-blue-600" />
                </div>
                <div className={`flex items-center text-xs ${jobsTrend.positive ? "text-green-600" : "text-red-600"}`}>
                  {jobsTrend.positive ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                  {jobsTrend.value}%
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{totals.jobs}</p>
              <p className="text-sm text-gray-500">Jobs Completed</p>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">£{totals.avg_job_value.toLocaleString()}</p>
              <p className="text-sm text-gray-500">Avg Job Value</p>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Clock className="w-5 h-5 text-orange-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {monthly.length > 0 ? 
                  `${Math.round(monthly.reduce((sum, m) => sum + (m.avg_duration_hours || 0), 0) / monthly.length)}h` : 
                  "0h"
                }
              </p>
              <p className="text-sm text-gray-500">Avg Duration</p>
            </div>
          </div>

          {/* Monthly Chart */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Monthly Revenue</h3>
            
            {/* Simple Bar Chart */}
            <div className="space-y-4">
              {monthly.map((month, idx) => {
                const maxRevenue = Math.max(...monthly.map(m => m.revenue), 1);
                const percentage = (month.revenue / maxRevenue) * 100;
                
                return (
                  <div key={idx} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{month.month_name}</span>
                      <span className="font-medium text-gray-900">£{month.revenue.toLocaleString()}</span>
                    </div>
                    <div className="h-8 bg-gray-100 rounded-lg overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-primary to-primary/70 rounded-lg transition-all duration-500 flex items-center justify-end pr-2"
                        style={{ width: `${Math.max(percentage, 5)}%` }}
                      >
                        {month.jobs_completed > 0 && (
                          <span className="text-xs text-white font-medium">{month.jobs_completed} jobs</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {monthly.length === 0 && (
              <div className="text-center py-8 text-gray-400">
                No data available yet
              </div>
            )}
          </div>

          {/* Monthly Breakdown Table */}
          <div className="bg-white rounded-xl shadow-sm border mt-6 overflow-hidden">
            <div className="p-4 border-b">
              <h3 className="font-semibold text-gray-900">Monthly Breakdown</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Month</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Revenue</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Jobs</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Avg Value</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Avg Duration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {monthly.map((month, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{month.month_name}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-green-600">
                        £{month.revenue.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-700">{month.jobs_completed}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-700">
                        £{month.avg_job_value.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-700">{month.avg_duration_hours}h</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {activeView === "sources" && (
        <>
          {/* Lead Sources Summary */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Source Distribution */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Lead Source Distribution</h3>
              
              <div className="space-y-3">
                {Object.entries(bySource).map(([source, data], idx) => {
                  const totalLeads = Object.values(bySource).reduce((sum, s) => sum + s.leads, 0);
                  const percentage = totalLeads > 0 ? Math.round((data.leads / totalLeads) * 100) : 0;
                  
                  return (
                    <div key={source} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">{formatSourceName(source)}</span>
                        <span className="font-medium">{data.leads} leads ({percentage}%)</span>
                      </div>
                      <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${sourceColors[idx % sourceColors.length]} rounded-full`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {Object.keys(bySource).length === 0 && (
                <p className="text-center text-gray-400 py-8">No source data available</p>
              )}
            </div>

            {/* Revenue by Source */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Revenue by Source</h3>
              
              <div className="space-y-4">
                {Object.entries(bySource)
                  .sort((a, b) => b[1].revenue - a[1].revenue)
                  .map(([source, data], idx) => (
                    <div key={source} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${sourceColors[idx % sourceColors.length]}`} />
                        <div>
                          <p className="font-medium text-gray-900">{formatSourceName(source)}</p>
                          <p className="text-xs text-gray-500">{data.won} jobs won</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-gray-900">£{data.revenue.toLocaleString()}</p>
                        <p className="text-xs text-gray-500">{data.conversion_rate}% conversion</p>
                      </div>
                    </div>
                  ))}
              </div>

              {Object.keys(bySource).length === 0 && (
                <p className="text-center text-gray-400 py-8">No revenue data available</p>
              )}
            </div>
          </div>

          {/* Source Performance Table */}
          <div className="bg-white rounded-xl shadow-sm border mt-6 overflow-hidden">
            <div className="p-4 border-b">
              <h3 className="font-semibold text-gray-900">Source Performance Comparison</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Source</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Total Leads</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Jobs Won</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Revenue</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Conversion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {Object.entries(bySource).map(([source, data], idx) => (
                    <tr key={source} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${sourceColors[idx % sourceColors.length]}`} />
                          <span className="text-sm text-gray-900">{formatSourceName(source)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-700">{data.leads}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-700">{data.won}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-green-600">
                        £{data.revenue.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          data.conversion_rate >= 50 ? "bg-green-100 text-green-700" :
                          data.conversion_rate >= 25 ? "bg-yellow-100 text-yellow-700" :
                          "bg-red-100 text-red-700"
                        }`}>
                          {data.conversion_rate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AnalyticsPanel;
